import React from 'react';
import PropTypes from 'prop-types';
import { Field } from '../reduxFormWrapper';

const calculateOnChange = (rawOnChange, customOnChange, reduxFormOnChange) => {
  const smartDefault = (...args) => {
    const result = reduxFormOnChange(...args);
    if (customOnChange) {
      customOnChange(...args);
    }
    return result;
  };
  return rawOnChange || smartDefault;
};

/*
This is a HOC that connects a form component to redux-form using <Field>

Please see full documentation at docs/react-components.md

`connectFormComponentToReduxForm` accepts two arguments:

1. FormComponent: The underlying FormComponent to be connected to redux-form. The underlying FormComponent
   should accept `value` and `onChange` props so that it can be controlled.
2. fieldProps: an optional object containing props to be passed to redux-form's `<Field>`.

The connected component returned from this HOC accepts various props:

- `name`: The name of the form field. This is passed along to `<Field>`, along with the `prefix` mentioned below
- `prefix`: optional prefix prepended to the field name (along with a dot).
- `onChange`: optional callback function to be called whenever the underlying form component emits a change. This function is called _in addition_ to the `onChange` function provided by `redux-form`
- `rawOnChange`: optional callback function be called whenever the underlying form component emits a change. This function is called _instead_ of the `onChange` function provided by `redux-form`. Most of the time you probably want to use `onChange`. However, if you want to prevent the default redux-form behavior and dispatch your own redux-form actions to the store, you can use `rawOnChange`.
- `validate`: a function for validation, which is passed through to redux-form's `<Field>`
- `defaultValue`: a value to pass to the form component when the value from redux-form is an empty string. This is to overcome a weakness in redux-form where it assumes an empty string should be the default value for all components.

*/
export default function connectFormComponentToReduxForm(FormComponent, fieldProps = {}) {
  const FormComponentConnectedToReduxForm = ({
    name,
    prefix,
    onChange,
    rawOnChange,
    defaultValue,
    validate,
    ...rest
  }) => {
    const prefixedName = prefix ? `${prefix}.${name}` : name;
    return (
      <Field
        name={prefixedName}
        validate={validate}
        {...fieldProps}
        render={({ input: reduxFormInputProps, ...otherReduxFormProps }) => (
          <FormComponent
            name={prefixedName}
            {...otherReduxFormProps}
            {...reduxFormInputProps}
            {...rest}
            value={
              reduxFormInputProps.value === '' && defaultValue
                ? defaultValue
                : reduxFormInputProps.value
            }
            onChange={calculateOnChange(rawOnChange, onChange, reduxFormInputProps.onChange)}
          />
        )}
      />
    );
  };
  FormComponentConnectedToReduxForm.displayName = `ReduxFormConnected(${FormComponent.displayName})`;
  FormComponentConnectedToReduxForm.propTypes = {
    name: PropTypes.string.isRequired,
  };
  return FormComponentConnectedToReduxForm;
}
